import isSafeRelativePath from '@/utils/urlSafety'

/**
 * The guided Rock setup's route and its URL helpers. Kept free of API and
 * store imports so the app shell (banner, sidebar, forced redirect) can use
 * them cheaply.
 */

/** Router pattern (`src/routes/index.jsx`). */
export const ROCK_SETUP_ROUTE = '/setup/rock/:connectionId'

/** `graphit_next` on the OAuth success return (backend T1). */
export const GRAPHIT_NEXT_PARAM = 'graphit_next'

/**
 * `/setup/rock/<id>`: the one path every entry point into the guided setup uses.
 *
 * @param {string} connectionId
 * @returns {string}
 */
export function rockSetupPath(connectionId) {
  return `/setup/rock/${encodeURIComponent(connectionId)}`
}

/** Router pattern of the guided Planning Center setup. */
export const PCO_SETUP_ROUTE = '/setup/planning-center/:connectionId'

/**
 * `/setup/planning-center/<id>`: the guided Planning Center setup.
 *
 * @param {string} connectionId
 * @returns {string}
 */
export function pcoSetupPath(connectionId) {
  return `/setup/planning-center/${encodeURIComponent(connectionId)}`
}

/** Router pattern of the guided ChurchStaq setup (the registry's `setup_route`). */
export const CHURCHSTAQ_SETUP_ROUTE = '/setup/churchstaq/:connectionId'

/**
 * `/setup/churchstaq/<id>`: the guided ChurchStaq setup.
 *
 * @param {string} connectionId
 * @returns {string}
 */
export function churchstaqSetupPath(connectionId) {
  return `/setup/churchstaq/${encodeURIComponent(connectionId)}`
}

/**
 * @param {string} pathname
 * @returns {boolean} whether `pathname` is the guided Rock setup route
 */
export function isRockSetupPathname(pathname) {
  return /^\/setup\/rock\/[^/]+\/?$/.test(pathname || '')
}

/**
 * @param {string} pathname
 * @returns {boolean} whether `pathname` is a guided setup (Rock, Planning Center or ChurchStaq)
 */
export function isGuidedSetupPathname(pathname) {
  return /^\/setup\/(rock|planning-center|churchstaq)\/[^/]+\/?$/.test(pathname || '')
}

/**
 * The in-app path a `graphit_next` value names, or null when it is not safe
 * to navigate to. A root-relative path must pass `isSafeRelativePath`; an
 * absolute URL (the Rock-started return carries the full app URL) is only
 * accepted on this app's own origin, and is reduced to its path.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
export function safeGraphitNext(value) {
  if (typeof value !== 'string' || value.length === 0) return null
  if (value.startsWith('/')) return isSafeRelativePath(value) ? value : null
  let url
  try {
    url = new URL(value)
  } catch {
    return null
  }
  if (url.origin !== window.location.origin) return null
  const path = `${url.pathname}${url.search}${url.hash}`
  return isSafeRelativePath(path) ? path : null
}

/** Where a guided setup sends a malformed id, another provider's connection or a missing one. */
export const INTEGRATIONS_PATH = '/settings/integrations'

/** Where "Save and exit" goes when the person didn't come from an app page. */
export const DEFAULT_EXIT_PATH = '/dashboards'

/**
 * The app page the person was on before the setup (a forced redirect or a
 * link passes it as `state.from`), when it is a safe in-app path that isn't
 * the setup itself; otherwise Home.
 *
 * @param {unknown} from
 * @returns {string}
 */
export function exitPathFrom(from) {
  if (typeof from !== 'string' || !isSafeRelativePath(from)) return DEFAULT_EXIT_PATH
  return isGuidedSetupPathname(from.split('?')[0]) ? DEFAULT_EXIT_PATH : from
}
